// eslint-disable-next-line max-classes-per-file
import { injectable } from 'inversify';
import { Transform } from 'class-transformer';
import { formatISO, parseISO } from 'date-fns';

import { Calendar } from '../calendar';

import { Filter, FilterType } from './filter.entity';
import { IsFilters } from './is-filters.validator';
import { filtersTransform } from './filters.transform';

const START_OF_DAY = {
  HOURS: 0,
  MINUTES: 0,
  SECONDS: 0,
};

const END_OF_DAY = {
  HOURS: 23,
  MINUTES: 59,
  SECONDS: 59,
};

/**
 * Класс содержит свойство 'filters'
 * @typedef FiltersClass
 * @type {object}
 * @property {Object.<string, string | string[] | boolean>} filters
 */

@injectable()
export class FilterService {
  constructor(private calendar: Calendar) {}

  /**
   * Возвращает новый класс с фильтрами
   * Расширяющая часть описана типом {@link FiltersClass}
   * @template {class} T
   * @param {abstract new () => T} Dto Расширяемый класс
   * @param {Array} filters Фильтры
   * @returns {new () => T & FiltersClass}
   */
  buildDto<T extends new (...args: any[]) => object>(
    Dto: T,
    filters: Filter[],
  ) {
    class FiltersDto extends Dto {
      @Transform(filtersTransform(filters))
      @IsFilters(filters)
      filters: {
        [key: string]: string | string[] | boolean;
      };
    }

    return FiltersDto;
  }

  /** Преобразует даты начала и окончания для Dateable значения
   * Если значение имеет тип отличный от Dateable, то никаких изменений не вносится
   * @param {number | boolean | string | Dateable} value Преобразуемое значение
   * @returns {void}
   *  */
  sanitizeDates(value: FilterType[string]) {
    if (typeof value !== 'object' || !(value.start ?? value.end)) {
      return;
    }

    if (value.start) {
      const startDate = parseISO(
        typeof value.start === 'string' ? value.start : formatISO(value.start),
      );

      value.start = new Date(
        Date.UTC(
          startDate.getFullYear(),
          startDate.getMonth(),
          startDate.getDate(),
          START_OF_DAY.HOURS,
          START_OF_DAY.MINUTES,
          START_OF_DAY.SECONDS,
        ),
      );
    }

    if (value.end) {
      const endDate = parseISO(
        typeof value.end === 'string' ? value.end : formatISO(value.end),
      );

      value.end = new Date(
        Date.UTC(
          endDate.getFullYear(),
          endDate.getMonth(),
          endDate.getDate(),
          END_OF_DAY.HOURS,
          END_OF_DAY.MINUTES,
          END_OF_DAY.SECONDS,
        ),
      );
    }
  }

  /** Преобразует даты начала и окончания для Dateable значения без из строки в дату
   * Если значение имеет тип отличный от Dateable, то никаких изменений не вносится
   * @param {number | boolean | string | Dateable} value Преобразуемое значение
   * @returns {void}
   *  */
  sanitizeStringToDate(value: FilterType[string]) {
    if (typeof value !== 'object' || !(value.start ?? value.end)) {
      return;
    }

    if (value.start) {
      value.start = new Date(value.start);
    }
    if (value.end) {
      value.end = new Date(value.end);
    }
  }
}
